import { useState } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
} from 'konsta/react'
import {
  Monitor,
  Users,
  User,
  DollarSign,
  XCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Types ---

type RoomStatus = 'active' | 'waiting' | 'full' | 'closed'

interface Room {
  id: string
  name: string
  blinds: string
  players: string
  status: RoomStatus
  host: string
}

// --- Mock Data ---

const mockRooms: Room[] = [
  { id: '1', name: '新手桌 #1', blinds: '$1/$2', players: '6/9', status: 'active', host: 'PWG 官方' },
  { id: '2', name: '中級桌 #1', blinds: '$2/$5', players: '7/9', status: 'active', host: 'PWG 官方' },
  { id: '3', name: '高額桌 VIP', blinds: '$25/$50', players: '8/9', status: 'active', host: '王大明' },
  { id: '4', name: '速戰桌 Turbo', blinds: '$2/$5', players: '6/6', status: 'full', host: 'PWG 官方' },
  { id: '5', name: 'David 的私人桌', blinds: '$5/$10', players: '4/6', status: 'waiting', host: 'David Chen' },
  { id: '6', name: 'PLO 新手桌', blinds: '$1/$2', players: '0/6', status: 'closed', host: 'PWG 官方' },
]

// --- Helpers ---

function getStatusConfig(status: RoomStatus) {
  switch (status) {
    case 'active':
      return { label: '進行中', color: '#34C759', bgClass: 'bg-[#34C759]/15' }
    case 'waiting':
      return { label: '等待中', color: '#FF9500', bgClass: 'bg-[#FF9500]/15' }
    case 'full':
      return { label: '已滿', color: '#FF3B30', bgClass: 'bg-[#FF3B30]/15' }
    case 'closed':
      return { label: '已關閉', color: '#8E8E93', bgClass: 'bg-[#8E8E93]/15' }
  }
}

function StatusBadge({ status }: { status: RoomStatus }) {
  const config = getStatusConfig(status)
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium',
        config.bgClass,
      )}
      style={{ color: config.color }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full mr-1"
        style={{ backgroundColor: config.color }}
      />
      {config.label}
    </span>
  )
}

// --- Component ---

export function AdminRoomsScreen() {
  const [rooms] = useState(mockRooms)

  const handleCloseRoom = (room: Room) => {
    console.log(`[Admin] Close room: ${room.id} (${room.name})`)
  }

  // Count stats
  const activeCount = rooms.filter((r) => r.status === 'active').length
  const waitingCount = rooms.filter((r) => r.status === 'waiting').length
  const fullCount = rooms.filter((r) => r.status === 'full').length
  const closedCount = rooms.filter((r) => r.status === 'closed').length

  return (
    <>
      <Navbar title="房間監控" />

      {/* Summary stats */}
      <Block className="!px-4 !mt-2 !mb-0">
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: '進行中', count: activeCount, color: '#34C759' },
            { label: '等待中', count: waitingCount, color: '#FF9500' },
            { label: '已滿', count: fullCount, color: '#FF3B30' },
            { label: '已關閉', count: closedCount, color: '#8E8E93' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl p-3 shadow-sm text-center">
              <p className="text-xl font-bold" style={{ color: stat.color }}>
                {stat.count}
              </p>
              <p className="text-[11px] text-[#8E8E93]">{stat.label}</p>
            </div>
          ))}
        </div>
      </Block>

      {/* Room count */}
      <BlockTitle className="!mt-4">
        所有房間 ({rooms.length})
      </BlockTitle>

      {/* Room list */}
      <List strongIos insetIos>
        {rooms.map((room) => {
          const statusConfig = getStatusConfig(room.status)
          return (
            <ListItem
              key={room.id}
              media={
                <div
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center',
                    statusConfig.bgClass,
                  )}
                >
                  <Monitor className="w-5 h-5" style={{ color: statusConfig.color }} />
                </div>
              }
              title={
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{room.name}</span>
                </div>
              }
              subtitle={
                <span className="text-[#8E8E93] text-[12px] flex items-center gap-2">
                  <span className="flex items-center gap-0.5">
                    <DollarSign className="w-3 h-3" />
                    {room.blinds}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Users className="w-3 h-3" />
                    {room.players}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <User className="w-3 h-3" />
                    {room.host}
                  </span>
                </span>
              }
              after={
                <div className="flex flex-col items-end gap-1.5">
                  <StatusBadge status={room.status} />
                  {room.status !== 'closed' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCloseRoom(room)
                      }}
                      className="text-[11px] text-[#FF3B30] flex items-center gap-0.5"
                    >
                      <XCircle className="w-3 h-3" />
                      關閉
                    </button>
                  )}
                </div>
              }
            />
          )
        })}
      </List>

      {/* Bottom padding */}
      <div className="h-8" />
    </>
  )
}
